import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import Button from 'flow/components/ui-components/Button';
import RouteLink from 'flow/components/ui-components/RouteLink';
import { SocialIntegration as SocialIntegrationType } from 'flow/types/SocialIntegration';
import {
    createNewAuthProcess,
    fetchSocialIntegrationSettings,
} from 'flow/actions/social-integration-actions';
import {
    createReturnUrl,
    getNbrSyncedContactsDescription,
} from 'flow/utils/social-integration-utils';
import { generatePath } from 'flow/utils/router-utils';

import { TypedMap } from 'flow/types/TypedMap';
import { routes } from 'flow/types/routes';
import { List } from 'immutable';

import SettingRow from '../SettingRow';
import Loading from 'flow/components/ui-components/Loading';
import { useParams } from 'react-router-dom';
import { ExtensionKey } from 'flow/types/EntityKeys';

interface Props {
    integration: 'google' | 'microsoft';
    data?: TypedMap<SocialIntegrationType>;
}

const SocialIntegration = ({ integration, data }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    useEffect(() => {
        dispatch(fetchSocialIntegrationSettings());
    }, []);

    const socialIntegrationSettings = useAppSelector((state) =>
        state.socialIntegration.getIn([
            'socialIntegrationSettings',
            integration,
        ]),
    );

    if (!extensionKey) {
        return <Loading />;
    }

    if (
        !data ||
        (!data?.get('clientAuthInProgress', false) && !data?.get('email'))
    ) {
        return (
            <div className="group flex items-center justify-between rounded-sm bg-white p-2 hover:bg-gray-100">
                <div className="flex items-center gap-x-3">
                    <div className="flex size-9 items-center justify-center rounded-sm bg-gray-100 group-hover:bg-gray-200">
                        <Icon iconKey={integration} />
                    </div>
                    <div className="capitalize">{integration}</div>
                </div>

                <div className="reveal-on-hover-or-focus block">
                    <Button
                        color="ghost"
                        onClick={() =>
                            dispatch(
                                createNewAuthProcess(
                                    integration,
                                    encodeURIComponent(
                                        createReturnUrl(
                                            extensionKey,
                                            integration,
                                        ),
                                    ),
                                    extensionKey,
                                ),
                            )
                        }
                    >
                        {i18next.t(
                            'settings.social_integration.connect_account',
                        )}
                    </Button>
                </div>
            </div>
        );
    }

    const getInfo = () => {
        const syncTypes = data.get('syncTypes', List());
        if (!['contact', 'calendar'].some((type) => syncTypes.includes(type))) {
            return null;
        }

        if (!data.get('lastSyncString')) {
            return i18next.t(
                'settings.social_integration.synchronisation.in_progress',
            );
        }

        const description = [];

        if (syncTypes.includes('contacts')) {
            description.push(
                getNbrSyncedContactsDescription(
                    data.get('contactCount'),
                    i18next,
                ),
            );
        }

        if (syncTypes.includes('calendar')) {
            description.push(
                i18next.t('settings.social_integration.one_calendar'),
            );
        }

        return description.join(', ');
    };

    if (socialIntegrationSettings?.get('clientAuthInProgress')) {
        return (
            <SettingRow cta={<Loading size={32} disableTimeout={true} />}>
                {i18next.t(`settings.social_integration.${integration}`)}
            </SettingRow>
        );
    }

    return (
        <RouteLink
            to={generatePath(
                routes['settings.account.socialintegration.integration'],
                {
                    extensionKey,
                    syncSource: integration,
                },
            )}
            description={getInfo()}
            className="group"
        >
            <div className="flex items-center gap-x-3">
                <div className="flex size-9 items-center justify-center rounded-sm bg-gray-100 group-hover:bg-gray-200">
                    <Icon iconKey={integration} />
                </div>

                <div>
                    {data.get('email') ||
                        i18next.t(`settings.social_integration.${integration}`)}
                </div>
            </div>
        </RouteLink>
    );
};

export default SocialIntegration;
